import { AlertCircle, ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { AuthShell } from "../components/AuthShell";
import { OTP_LENGTH, RESEND_COOLDOWN_SECONDS } from "../constants/register.constants";
import { authService } from "../services/authService";

const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local.at(-1)}@${domain}`;
};

export const ResetCodePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const { t } = useTranslations("login");

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (!email) {
      navigate("/auth/forgot-password", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const handleVerify = async () => {
    if (code.length !== OTP_LENGTH || isLoading) return;
    setError(null);
    setIsLoading(true);
    try {
      await authService.verifyResetCode(email, code);
      navigate(`/auth/forgot-password/new?email=${encodeURIComponent(email)}`, {
        state: { code },
      });
    } catch (err) {
      setError(HttpApiService.getErrorMessage(err) || t("fp_error_invalid_code"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    try {
      await authService.requestPasswordReset(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(HttpApiService.getErrorMessage(err) || t("fp_error_network"));
    }
  };

  return (
    <AuthShell>
      <div className="flex flex-col items-center w-full max-w-md mx-auto">
        <div className="flex flex-col items-center gap-4 mb-8 animate-fade-down">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative bg-primary/10 p-2 rounded-full">
              <Mail className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{t("fp_verify_title")}</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              {t("fp_verify_subtitle").replace("{email}", maskEmail(email))}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6 w-full animate-fade-up">
          <div className="flex flex-col items-center gap-3 w-full">
            <ColocaOtpInput
              autoFocus
              value={code}
              onValueChange={setCode}
              length={OTP_LENGTH}
              disabled={isLoading}
              invalid={!!error}
              aria-label={t("fp_verify_code_label")}
              onComplete={handleVerify}
            />
            <p className="text-xs text-muted-foreground">{t("fp_verify_code_hint")}</p>
          </div>

          {error && (
            <div className="animate-shake">
              <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
            </div>
          )}

          <ColocaButton
            variant="primary"
            size="lg"
            onClick={handleVerify}
            loading={isLoading}
            disabled={code.length !== OTP_LENGTH || isLoading}
            className="h-12 w-full text-base font-semibold"
            iconTrailing={ArrowRight}
          >
            {t("fp_verify_submit")}
          </ColocaButton>

          <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
            <span>{t("fp_verify_resend")}</span>
            {cooldown > 0 ? (
              <span className="font-mono">
                {t("fp_verify_resend_cooldown").replace("{seconds}", String(cooldown))}
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="font-semibold text-primary transition-colors hover:underline"
              >
                {t("fp_request_submit")}
              </button>
            )}
          </div>

          <div className="text-center">
            <Link
              to="/auth/forgot-password"
              className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("fp_verify_change_email")}
            </Link>
          </div>
        </div>
      </div>
    </AuthShell>
  );
};

export default ResetCodePage;
